/**
 * Fallback base for the shared asset-library CDN, used when no host is present
 * (studio preview, playground) or the host predates the assetLibrary RPC.
 * Mirrors ASSET_CDN_BASE in studio/packages/shared/src/wire.ts, which the SDK
 * cannot import — keep the two in sync.
 */
export const DEFAULT_ASSET_LIBRARY_BASE_URL = 'https://storage.googleapis.com/run-asset-library'

/**
 * The default URL prefix for a pinned asset-pack version. Shared by the mock
 * and the RPC fallback so both hostless paths produce identical URLs.
 */
export function defaultPackBaseUrl(packId: string, version: string): string {
  return `${DEFAULT_ASSET_LIBRARY_BASE_URL}/packs/${packId}@${version}`
}

/**
 * The single surface for platform-provided assets: referenced asset packs
 * (resolved to CDN URLs) and shared asset bundles (loaded as bytes, embedded
 * host copies preferred). Also exposed as the deprecated `sharedAssets`
 * namespace, which routes here.
 */
export interface AssetLibraryApi {
  /**
   * Resolve the URL prefix for a pinned asset-pack version. Append a file's
   * pack-relative path to load it: `${base}/models/wall.glb`. The prefix is
   * host-resolved so the platform can relocate the asset CDN without breaking
   * published games; the returned URLs are immutable per (packId, version).
   */
  getPackBaseUrl(packId: string, version: string): Promise<string>

  /**
   * Load a shared asset bundle as raw bytes. Hosts serve an embedded copy
   * from disk when one ships in the app; otherwise the bundle is fetched
   * from the CDN at `{game}/{bundleKey}.{fileType}`. Rejects with a generic
   * error when both paths fail.
   */
  loadAssetsBundle(game: string, bundleKey: string, fileType?: string): Promise<ArrayBuffer>
}
